import { styled, css } from '../../../../modules';
import { keyframes } from 'styled-components';

export const PageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;
    width: 100%;
    max-width: calc(100vh * 3 / 4);
    background-color: #ffffff;
    overflow: hidden;
`;

export const Header = styled.header`
    width: 100%;
    background-color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px 24px;
    min-height: 80px;
    flex-shrink: 0;
`;

export const LogoImage = styled.img`
    max-height: 48px;
    max-width: 180px;
    object-fit: contain;
`;

export const ProposalHeader = styled.div<{ $backgroundColor?: string; $padBottom?: number }>`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 20px;
    padding: ${({ $padBottom }) => `24px 24px ${$padBottom ?? 90}px`};
    background: ${({ $backgroundColor }) => `
        radial-gradient(
            circle at top left,
            ${hexToRgba($backgroundColor || '#ffffff', 0.82)} 0%,
            ${hexToRgba($backgroundColor || '#ffffff', 0.92)} 100%
        )
    `};
    min-height: 80px;
    flex-shrink: 0;
`;

export const HeaderBodyText = styled.p`
    font-size: 14px;
    color: rgba(255, 255, 255, 0.85);
    margin: 0;
    line-height: 1.6;
    text-align: center;

    strong {
        color: #ffffff;
        font-weight: 700;
    }
`;

export const CardWrapper = styled.div<{ $color?: string }>`
    position: relative;
    display: flex;
    justify-content: center;
    margin-bottom: 16px;
    padding: 4px;
    margin-top: 16px;
    border-radius: 16px;
    background-color: ${({ $color }) => $color || '#ffffff'}33;
    border: 1px solid ${({ $color }) => $color || '#ffffff'};
`;

export const GlassyBadge = styled.div<{ $color?: string }>`
    position: absolute;
    top: -48px;
    right: -28px;
    width: 72px;
    height: 72px;
    border-radius: 18px;
    background: rgba(255, 255, 255, 0.12);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 1px solid ${({ $color }) => hexToRgba($color || '#ffffff', 0.5)};
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
`;

export const CheckImage = styled.img`
    width: 44px;
    height: 44px;
`;

export const HeaderTitle = styled.h1`
    font-size: 23px;
    font-weight: 400;
    color: #ffffff;
    margin: 0;
    line-height: 1.35;
    text-align: center;

    strong {
        font-weight: 800;
    }
`;

function hexToRgba(hex: string, alpha: number): string {
    let c = hex.replace('#', '');
    if (c.length === 3) {
        c = c.split('').map((x) => x + x).join('');
    }
    const num = parseInt(c, 16);
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    return `rgba(${r},${g},${b},${alpha})`;
}

export const ScrollContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    width: 100%;
    overflow-y: auto;
    padding-bottom: 8px;
`;

export const HeroSection = styled.div`
    width: 100%;
    padding: 8px 24px 4px;
    margin: 16px 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
`;

export const Title = styled.h1<{ $accentColor?: string }>`
    font-size: 23px;
    font-weight: 400;
    width: 100%;
    color: ${({ $accentColor }) => $accentColor || '#0e3573'};
    margin: 0;
    line-height: 1.35;
    text-align: center;

    strong {
        font-weight: 800;
    }
`;

export const MessageSection = styled.div`
    width: 100%;
    padding: 16px 24px;
`;

export const BodyText = styled.p<{ $accentColor?: string }>`
    font-size: 14px;
    color: #4a5568;
    margin: 0;
    line-height: 1.6;

    strong {
        color: ${({ $accentColor }) => $accentColor || '#0e3573'};
        font-weight: 700;
    }
`;

export const CardSection = styled.div`
    width: 100%;
    padding: 12px 24px 16px;
    margin-bottom: 16px;
    display: flex;
    justify-content: center;
`;

export const CardSectionOverlap = styled.div`
    width: 100%;
    padding: 0 24px 16px;
    display: flex;
    justify-content: center;
    margin-top: -72px;
    z-index: 2;
`;

export const AdvantagesSection = styled.div`
    width: 100%;
    padding: 8px 24px 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

export const AdvantagesTitle = styled.h3<{ $accentColor?: string }>`
    font-size: 18px;
    font-weight: 700;
    color: ${({ $accentColor }) => $accentColor || '#0e3573'};
    margin: 8px 0 0 0;
`;

export const AdvantageHighlight = styled.div<{ $borderColor: string; $textColor: string }>`
    background-color: transparent;
    border: 1px solid ${({ $borderColor }) => $borderColor};
    border-radius: 12px;
    padding: 12px 20px;
    font-size: 14px;
    font-weight: 500;
    color: ${({ $textColor }) => $textColor};
    text-align: center;
    background-color: ${({ $borderColor }) => $borderColor}14;

    strong {
        font-weight: 700;
    }
`;

export const AdvantagesGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    justify-content: center;
`;

export const AdvantageChip = styled.div<{ $borderColor: string; $textColor: string }>`
    background-color: transparent;
    border: 1px solid ${({ $borderColor }) => $borderColor};
    border-radius: 12px;
    padding: 8px 18px;
    font-size: 13px;
    font-weight: 500;
    color: ${({ $textColor }) => $textColor};
    background-color: ${({ $borderColor }) => $borderColor}14;
`;

export const DataCardContainer = styled.div`
    width: 100%;
    padding: 0 20px;
`;

export const DisclaimerSection = styled.div`
    width: 100%;
    margin-top: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const CheckboxWrapper = styled.div`
    width: 100%;
    padding: 16px 20px 8px;
    margin-top: 8px;
`;

export const FooterSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    gap: 12px;
    padding: 16px 20px;
    background-color: #ffffff;
    border-top: 1px solid #e2e8f0;
    flex-shrink: 0;
`;

export const DualButtonRow = styled.div`
    display: flex;
    gap: 12px;
    width: 100%;
`;

export const SecondaryButton = styled.button`
    flex: 1;
    padding: 16px 8px;
    border: none;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    outline: none;
    cursor: pointer;
    transition: opacity 0.2s ease;
    background-color: #e2e8f0;
    color: #4a5568;

    &:hover {
        opacity: 0.85;
    }
`;

export const WithdrawalCardsRow = styled.div`
    display: flex;
    gap: 12px;
    width: 100%;
    padding: 8px 20px;
`;

export const WithdrawalCard = styled.div<{
    $variant: 'primary' | 'secondary';
    $accentColor: string;
}>`
    flex: 1;
    border-radius: 12px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    background-color: ${({ $variant, $accentColor }) =>
        $variant === 'primary' ? $accentColor + '14' : '#f7fafc'};
    border: 1px solid
        ${({ $variant, $accentColor }) =>
            $variant === 'primary' ? $accentColor + '33' : '#e2e8f0'};
`;

export const WithdrawalValue = styled.span<{ $accentColor?: string }>`
    font-size: 18px;
    font-weight: 800;
    color: ${({ $accentColor }) => $accentColor || '#0e3573'};
`;

export const WithdrawalLabel = styled.span`
    font-size: 11px;
    color: #718096;
    line-height: 1.4;
`;

export const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 0 20px;
`;

export const DisclaimerWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: -8px;
`;

export const Separator = styled.div`
    width: 100%;
    height: 1px;
    background-color: #e2e8f0;
    margin: 16px 0;
`;

const fadeIn = keyframes`
    from { opacity: 0; }
    to   { opacity: 1; }
`;

const fadeOut = keyframes`
    from { opacity: 1; }
    to   { opacity: 0; }
`;

const slideUp = keyframes`
    from { transform: translateY(100%); }
    to   { transform: translateY(0); }
`;

const slideDown = keyframes`
    from { transform: translateY(0); }
    to   { transform: translateY(100%); }
`;

export const ModalOverlay = styled.div<{ $closing?: boolean }>`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    z-index: 100;
    ${({ $closing }) =>
        $closing
            ? css`animation: ${fadeOut} 0.3s ease forwards;`
            : css`animation: ${fadeIn} 0.3s ease forwards;`}
`;

export const ModalSheet = styled.div<{ $closing?: boolean }>`
    background: #ffffff;
    border-radius: 20px 20px 0 0;
    padding: 28px 24px 36px;
    width: 100%;
    max-width: calc(100vh * 3 / 4);
    display: flex;
    flex-direction: column;
    gap: 16px;
    ${({ $closing }) =>
        $closing
            ? css`animation: ${slideDown} 0.3s ease forwards;`
            : css`animation: ${slideUp} 0.3s ease forwards;`}
`;

export const ModalTitle = styled.h2<{ $accentColor?: string }>`
    font-size: 20px;
    font-weight: 700;
    color: ${({ $accentColor }) => $accentColor || '#0e3573'};
    margin: 0;
`;

export const ModalText = styled.p`
    font-size: 14px;
    color: #4a5568;
    margin: 0;
    text-align: justify;
    line-height: 1.6;
`;

export const ModalButtonRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 8px;
`;